import { ref, computed, watch } from 'vue';
import { defineStore } from 'pinia';
import { useApi } from '@/plugins/api';
import { useRouter, useRoute } from 'vue-router';
import { useStoreTranslation } from "@/stores/translation";
import { useStoreCart } from "@/stores/cart";
import { useStoreModal } from "@/stores/modal";
import { useStoreRestaurant } from "@/stores/restaurant";
import { useHistory } from "@/stores/history";

import Cookies from 'js-cookie';
import { logger } from '@/plugins/logger';
import { useStoreCustomer } from './customer';

const LOGGER_TAG = 'app';
export const useStoreApp = defineStore('app', () => {
    const mobile = ref(false);
    const api = useApi();
    const config = ref(null);
    const router = useRouter();
    const route = useRoute();

    const { translate } = useStoreTranslation();
    const cartStore = useStoreCart();
    const modalStore = useStoreModal();
    const rStore = useStoreRestaurant();
    const historyStore = useHistory();
    const customerStore = useStoreCustomer();
    const isReady = ref(false);

    let isCordova = ref(false);
    let isDevelop = process.env.NODE_ENV !== 'production';

    let isMobile = computed(() => {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
        );
    });

    let isAppMobile = computed(() => {
        return isMobile && isCordova.value && mobile;
    });

    let os = computed(() => {
        var userAgent = navigator.userAgent;

        // Windows Phone must come first because its UA also contains "Android"
        if (userAgent.match(/Windows Phone/i)) {
            return "Windows Phone";
        }

        if (userAgent.match(/(Android)/)) {
            return 'android';
        }

        if (userAgent.match(/(iPhone|iPod|iPad)/)) {
            return 'ios';
        }

        return 'web';
    });

    let isIos = computed(() => {
        return os.value === 'ios';
    });

    let isAndroid = computed(() => {
        return os.value === 'android';
    });


    function addCordovaScript() {
        return new Promise((resolve, reject) => {
            logger.info(LOGGER_TAG, 'addCordovaScript', config.value.cordova);

            const cordovaScript = document.createElement('script');
            cordovaScript.type = 'text/javascript';
            cordovaScript.src = config.value.cordova;

            // Risolve la promise quando lo script è caricato
            cordovaScript.onload = () => {
                logger.info(LOGGER_TAG, 'Cordova script caricato con successo');
                resolve();
            };
            // Rifiuta la promise in caso di errore di caricamento
            cordovaScript.onerror = (err) => {
                logger.error(LOGGER_TAG, 'Errore nel caricamento del Cordova script', err);
                reject(new Error(`Failed to load script: ${cordovaScript.src}`));
            };

            document.head.appendChild(cordovaScript);
        });
    }

    async function getConfig() {
        let queryParams = route.query;
        try {
            let { data } = await api.get('/app', { params: queryParams });
            config.value = data;
            return data;
        } catch (error) {
            logger.error(LOGGER_TAG, error);
        }
    }

    function initialize() {
        logger.info(LOGGER_TAG, 'initialize');
        document.addEventListener('deviceready', onDeviceReady, false);
    };

    function handleBackButton(event) {
        logger.info(LOGGER_TAG, 'handleBackButton', modalStore.isOpen);
        if (modalStore.isOpen) {
            return;
        }

        logger.info(LOGGER_TAG, 'handleBackButton', event);
        historyStore.updateAnimation("slide-left");
        event.preventDefault();
        event.stopPropagation();
        router.back();
    }

    function onDeviceReady() {
        isCordova.value = true;

        // Hide splashscreen, if supported by the Cordova app
        if (navigator.splashscreen) {
            navigator.splashscreen.hide();
        }

        document.addEventListener('backbutton', handleBackButton, false);

        if (cordova.InAppBrowser) {
            logger.info(LOGGER_TAG, 'cordova.InAppBrowser');
            window.open = cordova.InAppBrowser.open;
        }

        if (window.NavigationBar && config.value.statusBarBgColor) {
            NavigationBar?.backgroundColorByHexString(config.value.statusBarBgColor);
        }

        //alert("Cordova DEVICEREADY");
        //alert(PushNotification);
        var push = PushNotification.init({
            'android': pushInitOptions('android'),
            'ios': pushInitOptions('ios'),
        });

        if (config.value.isFullHosted && needDeviceReg()) {	// register device here for the new Full Hosted Web Apps (custom apps)
            push.on('registration', function (data) {
                //alert('registration');
                //alert(JSON.stringify(data));
                // NOTA: Se disinstallo e reinstallo l'app la registrationId cambia
                var post = {};
                post.registration_id = data.registrationId;
                post.device = JSON.stringify(device);	// cordova-plugin-device

                api.post('/app/act/register_device', post).then((res) => {
                    let { ok, device_id } = res.data;
                    config.value.device_id = device_id;
                });
            });
        }

        push.on('notification', onPushNotification);
        push.on('error', function (e) {
            logger.error(LOGGER_TAG, 'notification error', e);
        });

        // Android: Check/Ask Notification Permission (for Android >= 13)
        androidNotificationPermission();

        // iOS + Facebook SDK: Advertiser Tracking Enabled (https://github.com/MaximBelov/cordova-plugin-fbsdk)
        if (config.value.isOpening && os.value == 'ios' && window.facebookConnectPlugin) {
            facebookConnectPlugin.setAdvertiserTrackingEnabled(true, function () {
                logger.info(LOGGER_TAG, 'facebookConnect - setAdvertiserTrackingEnabled success');
            }, function () {
                logger.error('facebookConnect - setAdvertiserTrackingEnabled failure');
            });
        }

        isReady.value = true;
        logger.info(LOGGER_TAG, 'onDeviceReady', config.value, cordova.version);
    }

    function onPushNotification(data) {
        logger.info(LOGGER_TAG, "on:notification", typeof data, data);

        var title = data.title;
        var message = data.message.replace(/<br[^>]*>/gi, '');
        var foreground = data.additionalData.foreground;	// se arriva quando sto usando l'app (dopo il login)
        var type = data.additionalData.type;

        if (type == 'order') {
            // Notification about an Order (order_timeout, order_confirmed, order_delivering, ...)
            ifFgBeep(data);

            var orderUid = data.additionalData.orderUid;
            var token = data.additionalData.token;

            if (foreground) {
                var buttons = [config.value.strings['show_tracking'], config.value.strings['ok']];
                msgConfirm(message, function () {
                    router.push({ path: '/tracking', query: { ouid: orderUid, token } });
                }, function () { }, title, buttons);
            } else {
                router.push({ path: '/tracking', query: { ouid: orderUid, token } });
            }
        } else if (type == 'coupon') {
            // Other type of notification
            ifFgBeep(data);
            msgConfirm(message, () => {
                var couponCode = data.additionalData.couponCode;
                logger.info(LOGGER_TAG, 'coupon-data', JSON.stringify(data.additionalData), couponCode);
                try {
                    cartStore.setCoupon(couponCode);
                    modalStore.open('alert', { type: 'info', title: 'Coupon', message: "Coupon applicato correttamente" });
                } catch (e) {
                    console.error(e);
                    modalStore.open('alert', { type: 'warning', message: "Ops! Sembra ci sia stato un errore nell'applicazione del coupon. Riprova a breve." });
                }
            }, () => { logger.info(LOGGER_TAG, 'sono qui'); }, title, ['Applica', 'Chiudi']);
        } else {
            ifFgBeep(data);
            msgAlert(message, null, title);
        }
    }

    // PushNotification init options
    function pushInitOptions(os) {
        var options = {};
        switch (os) {
            case 'android':
                // cordova-plugin-local-notifications: al momento NON installato
                //options.clearNotifications = false;	// https://github.com/katzer/cordova-plugin-local-notifications/issues/1658
                if (config.value.cdv < 2) {	// old apps
                    options.senderID = config.value.androidSenderId;
                }
                // status bar notification icon (if this icon is not found in the apk, uses the main icon)
                options.icon = 'notification';
                // in case this Android version/mod supports just "monochrome" notification icons,
                // this is the color to be used in the notification drawer (for icon bg or fg, it depends to the Android version/mod)
                if (config.value.notificationIconColor) {
                    options.iconColor = config.value.notificationIconColor;
                }
                break;
            case 'ios':
                options.alert = true;
                options.badge = true;
                options.sound = true;
                break;
        }
        return options;
    }

    // Need device (re-)registration?
    // (registra all'apertura o dopo 60min dall'ultima registrazione)
    function needDeviceReg() {
        var refreshTime = 60 * 60 * 1000;	// refresh reg. every 60mins
        //var refreshTime = 60*1000;	// refresh reg. every 1mins	// to DEBUG
        //var refreshTime = 1000;	// refresh reg. every 1sec	// to DEBUG
        var nowTime = new Date().getTime();
        var lastRegTime = parseFloat(Cookies.get('appLastDeviceReg'));
        if (!lastRegTime || (nowTime - lastRegTime) >= refreshTime || config.value.isOpening) {
            Cookies.set('appLastDeviceReg', nowTime)
            return true;
        }
        return false;
    }

    // If app was in foreground (open, not in background and with active screen) when the notification arrived, emit a beep/vibration
    function ifFgBeep(data) {
        var foreground = data.additionalData.foreground;	// se arriva quando sto usando l'app
        if (foreground) {
            navigator.notification.beep(1);	// cordova-plugin-dialogs
            navigator.vibrate(750);			// cordova-plugin-vibration@2.1.6
        }
    }

    // Support notification permission on Android 13+
    // https://github.com/dpa99c/cordova-plugin-firebasex/issues/777
    function androidNotificationPermission() {
        if (!window.device) // cordova-plugin-device
            return;
        if (device.platform != 'Android')
            return;
        var androidVersion = parseInt(device.version, 10);
        if (androidVersion < 13)
            return;
        // if we arrive here, I'm on Android >= 13
        logger.info(LOGGER_TAG, "Device O.S. is Android 13+", androidVersion);

        if (!cordova.plugins || !cordova.plugins.permissions) // cordova-plugin-android-permissions (skip if not installed)
            return;

        var permissions = cordova.plugins.permissions;
        permissions.checkPermission(permissions.POST_NOTIFICATIONS, function (status) {
            if (status.hasPermission) {
                logger.info(LOGGER_TAG, "Android 13+ - app has POST_NOTIFICATIONS permission");
            } else {
                logger.info(LOGGER_TAG, "Android 13+ - app has NOT POST_NOTIFICATIONS permission - Going to ask for it!");
                permissions.requestPermission(permissions.POST_NOTIFICATIONS, function (status) {
                    logger.info(LOGGER_TAG, "requestPermission success - status.hasPermission:", status.hasPermission);
                }, function () {
                    logger.error(LOGGER_TAG, "requestPermission error");
                });
            }
        });
    }

    async function androidCameraPermission() {
        if (!window.device) // cordova-plugin-device
            return false;
        if (device.platform != 'Android')
            return false;
        var androidVersion = parseInt(device.version, 10);
        if (androidVersion < 13)
            return false;
        // if we arrive here, I'm on Android >= 13
        logger.info(LOGGER_TAG, "Device O.S. is Android 13+", androidVersion);

        if (!cordova.plugins || !cordova.plugins.permissions) // cordova-plugin-android-permissions (skip if not installed)
            return false;

        var permissions = cordova.plugins.permissions;
        return new Promise((resolve) => {
            permissions.checkPermission(permissions.CAMERA, function (status) {
                if (status.hasPermission) {
                    logger.info(LOGGER_TAG, "Android 13+ - app has CAMERA permission");
                    resolve(true);
                } else {
                    logger.info(LOGGER_TAG, "Android 13+ - app has NOT CAMERA permission - Going to ask for it!");
                    permissions.requestPermission(permissions.CAMERA, function (status) {
                        logger.info(LOGGER_TAG, "requestPermission success - status.hasPermission:", status.hasPermission);
                        resolve(status.hasPermission);
                    }, function () {
                        logger.error(LOGGER_TAG, "requestPermission error");
                        resolve(false);
                    });
                }
            });
        });
    }

    /**
     * cordova plugin add cordova-plugin-dialogs
     * @param msg
     * @param callback
     * @param title
     * @param buttonName
     */
    function msgAlert(msg, callback, title, buttonName) {
        if (title == undefined) title = "Attenzione!";
        try {	// cordova
            navigator.notification.alert(msg, callback, title, buttonName);
        } catch (err) {	// browser
            alert(msg);
            if (callback != undefined)
                callback();
        }
    }

    /**
     * cordova plugin add cordova-plugin-dialogs
     * @param msg
     * @param onConfirm
     * @param onCancel
     * @param title
     * @param buttonLabels
     */
    function msgConfirm(msg, onConfirm, onCancel, title, buttonLabels) {
        if (title == undefined) title = "Conferma";
        try {	// cordova
            navigator.notification.confirm(msg, function (index) {
                if (index == 1) {
                    if (onConfirm != undefined) onConfirm();
                } else {
                    if (onCancel != undefined) onCancel(index);
                }
            }, title, buttonLabels);
        } catch (err) {	// browser
            if (confirm(msg)) {
                if (onConfirm != undefined) onConfirm();
            } else {
                if (onCancel != undefined) onCancel();
            }
        }
    }

    function setStatusBarColorBasedOnRoute(route) {
        let color = config.value ? config.value.statusBarBgColor : '#ffffff';
        logger.info(LOGGER_TAG, 'setStatusBarColorBasedOnRoute | init', route, color, rStore.restaurant?.id);

        if (route === 'home') {
            // temporary fix for home page done for Andrea in 19/05/2025
            if (rStore.restaurant?.id === 290 || rStore.restaurant?.id === "290") {
                color = '#2a285d';
            }

            if (rStore.restaurant?.id === 697 || rStore.restaurant?.id === "697") {
                color = '#2a285d';
            }

            if (rStore.restaurant?.id === 249 || rStore.restaurant?.id === "249") {
                color = '#ef3e31';
            }
        }

        if (window.StatusBar) {
            logger.info(LOGGER_TAG, 'setStatusBarColorBasedOnRoute | setting', color);
            window.StatusBar.backgroundColorByHexString(color);
            if (color === '#ffffff') {
                StatusBar.styleDefault();
            } else {
                StatusBar.styleLightContent();
            }
        } else {
            logger.warn(LOGGER_TAG, 'setStatusBarColorBasedOnRoute | StatusBar not available');
        }
    }

    function setStatusBarColor(color) {
        logger.info(LOGGER_TAG, 'setStatusBarColor', color);
        if (window.StatusBar) {
            StatusBar.backgroundColorByHexString(color);
            if (color === '#ffffff') {
                StatusBar.styleDefault();
            } else {
                StatusBar.styleLightContent();
            }
        }
    }

    /**
     * Apre un URL nel browser appropriato (Cordova InAppBrowser o browser normale)
     * @param {string} url - L'URL da aprire
     * @param {Object} options - Opzioni aggiuntive
     * @param {string} options.target - Target del browser ('_blank', '_system', etc.)
     * @param {string} options.features - Caratteristiche per il browser Cordova
     * @returns {Window|null} - Riferimento alla finestra aperta (per _blank) o null
     */
    function openBrowser(url, options = {}) {
        let linkFeature = '';
        let linkTarget = options.target || '_blank';

        if (isCordova.value) {
            // Per _system manteniamo il comportamento originale
            // Per _blank permettiamo di mantenere il target per la comunicazione
            if (options.target !== '_blank') {
                linkTarget = '_system';
            }
            linkFeature = options.features || '';

            if (isIos.value) {
                linkFeature = linkFeature || 'location=no,hidespinner=no,zoom=no';
            } else {
                linkFeature = linkFeature || 'location=yes,toolbar=no,footer=no,hideurlbar=yes,toolbarcolor=#ffffff,hidespinner=no,toolbartranslucent=yes,closebuttoncolor=red,zoom=no';
            }
        }

        logger.info(LOGGER_TAG, 'openBrowser', url, { linkTarget, linkFeature });

        try {
            return window.open(url, linkTarget, linkFeature);
        } catch (error) {
            logger.error(LOGGER_TAG, 'Error opening browser:', error);
            return null;
        }
    }

    // watch customerStore.tracking and route.name to update status bar color
    watch([isReady, () => customerStore.tracking, () => route.name, () => rStore.restaurant, () => rStore.isLoading], () => {
        logger.info(LOGGER_TAG, 'watch customerStore.tracking and route.name', customerStore.tracking, route.name, rStore.isLoading, isReady.value, isCordova.value);
        if (!isCordova.value) {
            return;
        }

        if (!isReady.value || rStore.isLoading) {
            logger.warn(LOGGER_TAG, 'Store not ready yet, skipping status bar color update');
            setStatusBarColor('#ffffff');
            return;
        }

        if (customerStore.tracking && route.name === 'home') {
            setStatusBarColor(rStore.restaurant?.theme?.secondary || '#ffffff');
        } else {
            setStatusBarColorBasedOnRoute(route.name);
        }
    });

    return {
        isDevelop, isCordova, isMobile, isAppMobile, os, isIos, isAndroid, config,
        getConfig, initialize, androidCameraPermission, addCordovaScript,
        setStatusBarColor, setStatusBarColorBasedOnRoute, openBrowser,
    };
});
